import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Avatar,
  Divider,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Mention from "@tiptap/extension-mention";
import { ReactRenderer } from "@tiptap/react";
import tippy from "tippy.js";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import CodeIcon from "@mui/icons-material/Code";
import SendIcon from "@mui/icons-material/Send";
import { DateTime } from "luxon";
import { usePublicUserList } from "hooks/user/usePublicUserList";
import { Comment } from "@freetech/models";
import { useAuth } from "@/hooks/auth/useAuth";
import { useStakeholderBugReport } from "@/hooks/stakeholder/useStakeholderBugReport";
import { useFeatureRequest } from "@/hooks/featureRequest/useFeatureRequest";
import { useNonAdminClients } from "@/hooks/clients/useNonAdminClients";
import { UserInfo } from "@freetech/models";

interface CommentsWrapperProps {
  comments: Comment[];
  itemId: string;
  itemType: "bugReport" | "featureRequest";
  clientId: string;
  projectId?: string;
}

const MenuBar: React.FC<{ editor: any }> = ({ editor }) => {
  if (!editor) return null;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        mb: 1,
        borderBottom: 1,
        borderColor: "divider",
        pb: 1,
      }}
    >
      <Tooltip title="Bold">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBold().run()}
          color={editor.isActive("bold") ? "primary" : "default"}
        >
          <FormatBoldIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Italic">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          color={editor.isActive("italic") ? "primary" : "default"}
        >
          <FormatItalicIcon />
        </IconButton>
      </Tooltip>
      <Divider orientation="vertical" flexItem />
      <Tooltip title="Bullet List">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          color={editor.isActive("bulletList") ? "primary" : "default"}
        >
          <FormatListBulletedIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Numbered List">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          color={editor.isActive("orderedList") ? "primary" : "default"}
        >
          <FormatListNumberedIcon />
        </IconButton>
      </Tooltip>
      <Divider orientation="vertical" flexItem />
      <Tooltip title="Code">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          color={editor.isActive("codeBlock") ? "primary" : "default"}
        >
          <CodeIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};


interface MentionListProps {
  items: Array<{
    id: string;
    label: string;
    avatar?: string;
  }>;
  command: (props: any) => void;
}

interface MentionListRef {
  onKeyDown: ({ event }: { event: KeyboardEvent }) => boolean;
}

const MentionList = React.forwardRef<MentionListRef, MentionListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectItem = (index: number) => {
    const item = props.items[index];
    if (item) {
      props.command({ id: item.id, label: item.label });
    }
  };

  const upHandler = () => {
    setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % props.items.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  React.useEffect(() => setSelectedIndex(0), [props.items]);

  React.useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === "ArrowUp") {
        upHandler();
        return true;
      }

      if (event.key === "ArrowDown") {
        downHandler();
        return true;
      }

      if (event.key === "Enter") {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  return (
    <Paper
      elevation={3}
      sx={{
        padding: 1,
        maxHeight: 200,
        overflowY: 'auto',
        minWidth: 200,
      }}
    >
      {props.items.length ? (
        props.items.map((item, index) => (
          <Box
            key={item.id}
            onClick={() => selectItem(index)}
            sx={{
              padding: 1,
              cursor: 'pointer',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: index === selectedIndex ? 'action.hover' : 'transparent',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Avatar sx={{ width: 24, height: 24 }} src={item.avatar}>
              {item.label.charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="body2">{item.label}</Typography>
          </Box>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
          No users found
        </Typography>
      )}
    </Paper>
  );
});

MentionList.displayName = 'MentionList';


const CommentContent: React.FC<{ 
  content: string; 
  users?: UserInfo[];
}> = ({ content, users }) => {
  const renderContent = () => {

    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const mentions = doc.querySelectorAll('.mention');
    
    if (mentions.length === 0) {

      return <Box dangerouslySetInnerHTML={{ __html: content }} />;
    }


    const elements: React.ReactNode[] = [];
    let lastIndex = 0;
    const htmlContent = content;

    mentions.forEach((mention, index) => {
      const mentionId = mention.getAttribute('data-id');
      const mentionLabel = mention.textContent || '';
      const user = users?.find(u => u.id === mentionId);


      const mentionHtml = mention.outerHTML;
      const mentionIndex = htmlContent.indexOf(mentionHtml, lastIndex);

      if (mentionIndex > lastIndex) {

        const beforeContent = htmlContent.substring(lastIndex, mentionIndex);
        if (beforeContent) {
          elements.push(
            <Box 
              key={`before-${index}`}
              component="span"
              dangerouslySetInnerHTML={{ __html: beforeContent }} 
            />
          );
        }
      }


      if (user) {
        elements.push(
          <Tooltip
            key={`mention-${index}`}
            title={
              <Box sx={{ p: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Avatar
                    src={user.profilePicture || ""}
                    sx={{ width: 32, height: 32 }}
                  >
                    {user.firstName?.[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" color="inherit">
                      {user.firstName} {user.lastName}
                    </Typography>
                    <Typography variant="caption" color="inherit">
                      {user.email}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            }
            placement="top"
            arrow
          >
            <Box
              component="span"
              sx={{
                backgroundColor: 'primary.light',
                color: 'primary.contrastText',
                padding: '2px 6px',
                borderRadius: '4px',
                fontWeight: 'medium',
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'primary.main',
                }
              }}
            >
              {mentionLabel}
            </Box>
          </Tooltip>
        );
      } else {

        elements.push(
          <Box
            key={`mention-${index}`}
            component="span"
            sx={{
              backgroundColor: 'grey.300',
              color: 'text.primary',
              padding: '2px 6px',
              borderRadius: '4px',
              fontWeight: 'medium',
            }}
          >
            {mentionLabel}
          </Box>
        );
      }

      lastIndex = mentionIndex + mentionHtml.length;
    });


    if (lastIndex < htmlContent.length) {
      const remainingContent = htmlContent.substring(lastIndex);
      if (remainingContent) {
        elements.push(
          <Box 
            key="remaining"
            component="span"
            dangerouslySetInnerHTML={{ __html: remainingContent }} 
          />
        );
      }
    }

    return <Box>{elements}</Box>;
  };

  return (
    <Box sx={{ pl: 5 }}>
      {renderContent()}
    </Box>
  );
};

export const CommentsWrapper: React.FC<CommentsWrapperProps> = ({
  comments,
  itemId,
  itemType,
  clientId,
  projectId,
}) => {
  return (
    <Box 
      style={{ 
        padding: "24px", 
        backgroundColor: "#ffffff", 
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        border: "1px solid #e0e0e0"
      }}
    >
      {itemType === "bugReport" && (
        <BugReportComments
          comments={comments}
          bugReportId={itemId}
          clientId={clientId}
          projectId={projectId || ""}
        />
      )}
      {itemType === "featureRequest" && (
        <FeatureRequestComments
          comments={comments}
          featureRequestId={itemId}
          clientId={clientId}
        />
      )}
    </Box>
  );
};

interface BugReportCommentsProps {
  comments: Comment[];
  bugReportId: string;
  clientId: string;
  projectId: string;
}

const BugReportComments: React.FC<BugReportCommentsProps> = ({
  comments,
  bugReportId,
  clientId,
  projectId,
}) => {
  const { addComment, isAddingComment } = useStakeholderBugReport({
    clientId,
    projectId,
  });
  const { data: users } = usePublicUserList();
  const { userInfo } = useAuth();
  const { clients } = useNonAdminClients();
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);


  const mentionableUsers = useMemo(() => {
    const client = clients?.find(c => c.id === clientId);
    const accessibleUserIds = [
      ...(client?.associatedFreelancerIds || []),
      ...(client?.associatedStakeholderIds || []),
    ];
    
    return users?.filter(user => 
      accessibleUserIds.includes(user.id)
    ).map(user => ({
      id: user.id,
      label: `${user.firstName} ${user.lastName}`,
      avatar: user.profilePicture,
    })) || [];
  }, [users, clients, clientId]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: "Write a comment...",
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        suggestion: {
          items: ({ query }: { query: string }) => {
            return mentionableUsers.filter(item =>
              item.label.toLowerCase().startsWith(query.toLowerCase())
            ).slice(0, 5);
          },
          render: () => {
            let component: ReactRenderer<MentionListRef>;
            let popup: any;

            return {
              onStart: (props: any) => {
                component = new ReactRenderer(MentionList, {
                  props,
                  editor: props.editor,
                });

                if (!props.clientRect) {
                  return;
                }

                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                });
              },

              onUpdate(props: any) {
                component.updateProps(props);

                if (!props.clientRect) {
                  return;
                }

                popup[0].setProps({
                  getReferenceClientRect: props.clientRect,
                });
              },

              onKeyDown(props: any) {
                if (props.event.key === 'Escape') {
                  popup[0].hide();
                  return true;
                }

                return component.ref?.onKeyDown(props) || false;
              },

              onExit() {
                popup[0].destroy();
                component.destroy();
              },
            };
          },
        },
      }),
    ],
    onUpdate: ({ editor }) => {
      setIsEditorEmpty(editor.isEmpty);
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none",
      },
    },
  }, [mentionableUsers]);

  const handleSubmit = async () => {
    if (!editor || isEditorEmpty || isAddingComment) return;

    const content = editor.getHTML();
    

    const mentionedUserIds: string[] = [];
    const json = editor.getJSON();
    
    const extractMentions = (node: any) => {
      if (node.type === 'mention' && node.attrs?.id) {
        mentionedUserIds.push(node.attrs.id);
      }
      if (node.content) {
        node.content.forEach(extractMentions);
      }
    };
    
    if (json.content) {
      json.content.forEach(extractMentions);
    }

    const commentData: any = { 
      bugReportId, 
      content
    };
    
    if (mentionedUserIds.length > 0) {
      commentData.mentionedUserIds = mentionedUserIds;
    }
    
    await addComment(commentData);
    editor.commands.clearContent();
  };

  const getUserById = (userId: string) => {
    return users?.find((user) => user.id === userId);
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>
        Comments
      </Typography>

      {/* Comment List */}
      <Box sx={{ mb: 3 }}>
        {comments.map((comment) => {
          const user = getUserById(comment.userId);
          return (
            <Paper key={comment.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Avatar
                  src={user?.profilePicture || ""}
                  alt={user ? `${user.firstName} ${user.lastName}` : "Unknown"}
                  sx={{ width: 32, height: 32 }}
                >
                  {user?.firstName?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2">
                    {user
                      ? `${user.firstName} ${user.lastName}`
                      : "Unknown User"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {comment.createdAt}
                  </Typography>
                </Box>
              </Box>
              <CommentContent content={comment.content} users={users} />
            </Paper>
          );
        })}
      </Box>

      {/* Comment Editor */}
      {userInfo && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Avatar
              src={userInfo.profilePicture || ""}
              alt={`${userInfo.firstName} ${userInfo.lastName}`}
              sx={{ width: 32, height: 32 }}
            >
              {userInfo.firstName[0]}
            </Avatar>
            <Typography variant="subtitle2">
              {userInfo.firstName} {userInfo.lastName}
            </Typography>
          </Box>

          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
              mb: 2,
              "& .ProseMirror": {
                minHeight: "100px",
                outline: "none",
                "&:focus": {
                  outline: "none",
                },
              },
            }}
          >
            <MenuBar editor={editor} />
            <EditorContent editor={editor} />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              disabled={isEditorEmpty || isAddingComment}
              onClick={handleSubmit}
              endIcon={<SendIcon />}
            >
              {isAddingComment ? "Sending..." : "Send"}
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

interface FeatureRequestCommentsProps {
  comments: Comment[];
  featureRequestId: string;
  clientId: string;
}

const FeatureRequestComments: React.FC<FeatureRequestCommentsProps> = ({
  comments,
  featureRequestId,
  clientId,
}) => {
  const { addComment, isAddingComment } = useFeatureRequest();
  const { data: users } = usePublicUserList();
  const { userInfo } = useAuth();
  const { clients } = useNonAdminClients();
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);


  const mentionableUsers = useMemo(() => {
    const client = clients?.find(c => c.id === clientId);
    const accessibleUserIds = [
      ...(client?.associatedFreelancerIds || []),
      ...(client?.associatedStakeholderIds || []),
    ];
    
    return users?.filter(user => 
      accessibleUserIds.includes(user.id)
    ).map(user => ({
      id: user.id,
      label: `${user.firstName} ${user.lastName}`,
      avatar: user.profilePicture,
    })) || [];
  }, [users, clients, clientId]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: "Write a comment...",
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        suggestion: {
          items: ({ query }: { query: string }) => {
            return mentionableUsers.filter(item =>
              item.label.toLowerCase().startsWith(query.toLowerCase())
            ).slice(0, 5);
          },
          render: () => {
            let component: ReactRenderer<MentionListRef>;
            let popup: any;

            return {
              onStart: (props: any) => {
                component = new ReactRenderer(MentionList, {
                  props,
                  editor: props.editor,
                });

                if (!props.clientRect) {
                  return;
                }

                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                });
              },

              onUpdate(props: any) {
                component.updateProps(props);

                if (!props.clientRect) {
                  return;
                }

                popup[0].setProps({
                  getReferenceClientRect: props.clientRect,
                });
              },

              onKeyDown(props: any) {
                if (props.event.key === 'Escape') {
                  popup[0].hide();
                  return true;
                }

                return component.ref?.onKeyDown(props) || false;
              },

              onExit() {
                popup[0].destroy();
                component.destroy();
              },
            };
          },
        },
      }),
    ],
    onUpdate: ({ editor }) => {
      setIsEditorEmpty(editor.isEmpty);
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none",
      },
    },
  }, [mentionableUsers]);

  const handleSubmit = async () => {
    if (!editor || isEditorEmpty || isAddingComment) return;

    const content = editor.getHTML();
    

    const mentionedUserIds: string[] = [];
    const json = editor.getJSON();
    
    const extractMentions = (node: any) => {
      if (node.type === 'mention' && node.attrs?.id) {
        mentionedUserIds.push(node.attrs.id);
      }
      if (node.content) {
        node.content.forEach(extractMentions);
      }
    };
    
    if (json.content) {
      json.content.forEach(extractMentions);
    }

    const commentData: any = { 
      featureRequestId, 
      content
    };
    
    if (mentionedUserIds.length > 0) {
      commentData.mentionedUserIds = mentionedUserIds;
    }
    
    await addComment(commentData);
    editor.commands.clearContent();
  };

  const getUserById = (userId: string) => {
    return users?.find((user) => user.id === userId);
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>
        Comments
      </Typography>

      {/* Comment List */}
      <Box sx={{ mb: 3 }}>
        {comments.map((comment) => {
          const user = getUserById(comment.userId);
          return (
            <Paper key={comment.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Avatar
                  src={user?.profilePicture || ""}
                  alt={user ? `${user.firstName} ${user.lastName}` : "Unknown"}
                  sx={{ width: 32, height: 32 }}
                >
                  {user?.firstName?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2">
                    {user
                      ? `${user.firstName} ${user.lastName}`
                      : "Unknown User"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {comment.createdAt}
                  </Typography>
                </Box>
              </Box>
              <CommentContent content={comment.content} users={users} />
            </Paper>
          );
        })}
      </Box>

      {/* Comment Editor */}
      {userInfo && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Avatar
              src={userInfo.profilePicture || ""}
              alt={`${userInfo.firstName} ${userInfo.lastName}`}
              sx={{ width: 32, height: 32 }}
            >
              {userInfo.firstName[0]}
            </Avatar>
            <Typography variant="subtitle2">
              {userInfo.firstName} {userInfo.lastName}
            </Typography>
          </Box>

          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
              mb: 2,
              "& .ProseMirror": {
                minHeight: "100px",
                outline: "none",
                "&:focus": {
                  outline: "none",
                },
              },
            }}
          >
            <MenuBar editor={editor} />
            <EditorContent editor={editor} />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              disabled={isEditorEmpty || isAddingComment}
              onClick={handleSubmit}
              endIcon={<SendIcon />}
            >
              {isAddingComment ? "Sending..." : "Send"}
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
}; 